import { Skeleton, Card, Space, Divider, theme } from "antd";
import { useModel } from "@umijs/max";
import React, { useEffect, useState } from "react";

const MOBILE_LAYOUT_BREAKPOINT = 570;

/**
 * 局部内容区的骨架屏组件 (重用逻辑)
 */
const ContentSkeleton: React.FC<{ isMobile?: boolean }> = ({
  isMobile = false,
}) => {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        background: token.colorBgLayout,
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* 1. 模拟搜索栏区域 */}
      <Card
        style={{ marginBottom: "16px", borderRadius: 0 }}
        styles={{ body: { padding: "24px" } }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px 32px",
          }}
        >
          {[1, 2, 3].map((item) => (
            <div
              key={item}
              style={{
                width: isMobile ? "100%" : 180,
                flex: isMobile ? "0 0 100%" : "0 0 auto",
              }}
            >
              <Skeleton.Input
                active
                style={{ width: "100%", display: "block" }}
                size="default"
              />
            </div>
          ))}
          <div
            style={{
              width: isMobile ? "100%" : "auto",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Space>
              <Skeleton.Button active style={{ width: 80 }} />
              <Skeleton.Button active style={{ width: 80 }} />
            </Space>
          </div>
        </div>
      </Card>

      {/* 2. 模拟表格区域 */}
      <div
        style={{
          borderRadius: 0,
          flex: 1,
          background: token.colorBgContainer,
          padding: "24px 24px 24px 24px",
          marginTop: "16px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* <div
          style={{
            padding: "16px 0",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Skeleton.Input active style={{ width: 120 }} size="small" />
          <Skeleton.Button active style={{ width: 100 }} size="small" />
        </div> */}
        {/* <Divider /> */}
        <div style={{ margin: "0 0 16px 0", flex: 1 }}>
          <Skeleton
            active
            paragraph={{ rows: 12, width: "100%" }}
            title={false}
          />
        </div>
        <div
          style={{
            marginTop: "24px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Skeleton.Input active style={{ width: 350 }} size="small" />
        </div>
      </div>
    </div>
  );
};

// 在组件外部定义，确保在整个应用生命周期内持久化
// 只要侧边栏渲染出来过一次，这个值就会变为 true
let hasEverSeenLayout = false;

const Loading: React.FC = () => {
  const { token } = theme.useToken();
  // const { initialState, loading: modelLoading } = useModel("@@initialState");
  const [showSiderLoading, setShowSiderLoading] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= MOBILE_LAYOUT_BREAKPOINT;
  });

  // 初始化时直接进行同步检查 (SSR 友好处理)
  const [isInsideLayout, setIsInsideLayout] = useState(() => {
    if (hasEverSeenLayout) return true;
    if (typeof document !== "undefined") {
      const sider = document.querySelector(".ant-layout-sider");
      if (sider) {
        hasEverSeenLayout = true;
        return true;
      }
    }
    return false;
  });

  // 关键检测：通过 DOM 元素检测当前是否已经挂载到了 ProLayout 框架中
  useEffect(() => {
    if (isInsideLayout) {
      return undefined;
    }

    const checkSider = () => {
      const sider = document.querySelector(".ant-layout-sider");
      if (sider) {
        hasEverSeenLayout = true;
        setIsInsideLayout(true);
        return true;
      }
      return false;
    };

    if (!checkSider()) {
      // 如果没检测到，每 100ms 检测一次，直到检测到为止
      const timer = setInterval(() => {
        if (checkSider()) {
          clearInterval(timer);
        }
      }, 100);
      return () => clearInterval(timer);
    }

    return undefined;
  }, [isInsideLayout]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const syncSiderVisibility = () => {
      setShowSiderLoading(window.innerWidth >= MOBILE_LAYOUT_BREAKPOINT);
    };

    syncSiderVisibility();
    window.addEventListener("resize", syncSiderVisibility);
    return () => window.removeEventListener("resize", syncSiderVisibility);
  }, []);

  // 修正后的判定逻辑：
  // 1. 如果当前在登录页，直接展示内容骨架即可（登录页通常没有侧边栏）
  // 2. 如果不在登录页，且从未见过侧边栏（!isInsideLayout），说明是全屏刷新的外层加载
  const isLoginPath = ["/login", "/main_login"].includes(
    window.location.pathname,
  );
  const isOuterLoading = !isLoginPath && !isInsideLayout;
  const isMobile = !showSiderLoading;

  // ==================== 外层加载：模拟 ProLayout 整体结构的骨架屏 ====================
  if (isOuterLoading) {
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
          width: "100vw",
          background: token.colorBgLayout,
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 9999,
        }}
      >
        {showSiderLoading && (
          /* 左侧侧边栏骨架：仅在桌面宽度下显示 */
          <div
            style={{
              width: "256px",
              height: "100%",
              background: token.colorBgContainer,
              borderRight: `1px solid ${token.colorBorderSecondary}`,
              display: "flex",
              flexDirection: "column",
              flexShrink: 0,
            }}
          >
            {/* Logo 区域 */}
            <div
              style={{
                height: "64px",
                display: "flex",
                alignItems: "center",
                padding: "0 24px",
                marginBottom: "16px",
              }}
            >
              <Skeleton.Avatar
                active
                size="small"
                shape="square"
                style={{ marginRight: 12 }}
              />
              <Skeleton.Input active style={{ width: 100 }} size="small" />
            </div>

            {/* 菜单项列表 */}
            <div style={{ padding: "0 16px" }}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div
                  key={i}
                  style={{
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "8px",
                    padding: "0 8px",
                  }}
                >
                  <Skeleton.Avatar
                    active
                    size={16}
                    shape="square"
                    style={{ marginRight: 16 }}
                  />
                  <Skeleton.Input
                    active
                    style={{ width: i % 3 === 0 ? "80px" : "120px" }}
                    size="small"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 右侧主内容区骨架 */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* 顶部栏占位 */}
          <div
            style={{
              height: "56px",
              background: token.colorBgContainer,
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              padding: "0 24px",
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Skeleton.Avatar
                active
                size="small"
                style={{ marginRight: 12 }}
              />
              <Skeleton.Input active style={{ width: 60 }} size="small" />
            </div>
          </div>

          {/* 页面内容占位 */}
          <div style={{ flex: 1, overflow: "hidden" }}>
            <ContentSkeleton isMobile={isMobile} />
          </div>
        </div>
      </div>
    );
  }

  // ==================== 内层加载：仅页面内容骨架屏 ====================
  return <ContentSkeleton isMobile={isMobile} />;
};

export default Loading;
